/**
 * ✅ MARKDOWN TEXT RENDERER
 *
 * Safe markdown rendering with security (XSS prevention) and performance.
 * Supports: **bold**, *italic*, `code`, [links](url), ## headers, - lists
 *
 * @module MarkdownText
 */

import React from 'react';

import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

// ═══════════════════════════════════════════════════════════════════════════
// SECURITY CONFIG
// ═══════════════════════════════════════════════════════════════════════════

const ALLOWED_TAGS = [
  'a',
  'b',
  'strong',
  'i',
  'em',
  'code',
  'pre',
  'ul',
  'ol',
  'li',
  'p',
  'br',
  'span',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

// ═══════════════════════════════════════════════════════════════════════════
// CUSTOM COMPONENT STYLES
// ═══════════════════════════════════════════════════════════════════════════

const components: Components = {
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-amber-500 hover:text-amber-400 underline underline-offset-2 transition-colors"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-800 dark:text-white">{children}</strong>
  ),
  em: ({ children }) => <em className="italic text-slate-700 dark:text-white">{children}</em>,
  code: ({ children, className }) => {
    // Check if it's a code block (has language class) or inline code
    const isCodeBlock = className?.includes('language-');

    if (isCodeBlock) {
      return <code className={`${className} block overflow-x-auto`}>{children}</code>;
    }

    return (
      <code className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-800/50 text-amber-600 dark:text-amber-400 rounded text-[0.85em] font-mono">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-2 p-3 bg-slate-900 dark:bg-slate-950 rounded-lg border border-slate-700 dark:border-slate-800 overflow-x-auto text-sm">
      {children}
    </pre>
  ),
  h1: ({ children }) => (
    <h1 className="text-lg font-bold mt-4 mb-2 text-slate-800 dark:text-slate-100 first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-base font-semibold mt-3 mb-2 text-slate-800 dark:text-slate-100 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold mt-2 mb-1 text-slate-800 dark:text-slate-100 first:mt-0">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-medium mt-2 mb-1 text-slate-700 dark:text-white first:mt-0">
      {children}
    </h4>
  ),
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>,
  li: ({ children }) => <li className="text-slate-700 dark:text-white">{children}</li>,
  p: ({ children }) => <p className="mb-2 last:mb-0 text-slate-700 dark:text-white">{children}</p>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-amber-400 pl-3 my-2 italic text-slate-600 dark:text-white">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-100 dark:bg-slate-800/50">{children}</thead>,
  th: ({ children }) => (
    <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-white border border-slate-200 dark:border-slate-700">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-slate-600 dark:text-white border border-slate-200 dark:border-slate-700">
      {children}
    </td>
  ),
  hr: () => <hr className="my-4 border-slate-200 dark:border-slate-700" />,
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export interface MarkdownTextProps {
  content: string;
  className?: string;
  /**
   * If true, uses a simpler/faster renderer for list items
   * to avoid nested markdown complexity
   */
  simple?: boolean;
}

export const MarkdownText: React.FC<MarkdownTextProps> = React.memo(
  ({ content, className = '', simple = false }) => {
    // Early return for empty content
    if (!content || !content.trim()) return null;

    // For simple cases (like list items), use a lighter approach
    if (simple) {
      return <span className={className}>{renderSimpleMarkdown(content)}</span>;
    }

    return (
      <div className={`markdown-content ${className}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[
            rehypeHighlight,
            [
              rehypeSanitize,
              {
                tagNames: ALLOWED_TAGS,
                attributes: {
                  a: ['href', 'target', 'rel'],
                  code: ['className'],
                  span: ['className'],
                  pre: ['className'],
                },
              },
            ],
          ]}
          components={components}
          skipHtml={true}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  }
);

MarkdownText.displayName = 'MarkdownText';

// ═══════════════════════════════════════════════════════════════════════════
// SIMPLE INLINE MARKDOWN PARSER (No Dependencies)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Simple markdown parser for inline elements only (no nested structures).
 * Used for list items, table cells, etc. where full parsing is overkill.
 */
function renderSimpleMarkdown(text: string): React.ReactNode {
  if (!text) return null;

  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let buffer = '';
  let keyCounter = 0;

  const pushText = (str: string) => {
    if (str) parts.push(str);
  };

  const pushElement = (element: React.ReactNode) => {
    if (buffer) {
      pushText(buffer);
      buffer = '';
    }
    parts.push(element);
  };

  while (currentIndex < text.length) {
    const char = text[currentIndex];
    const nextChar = text[currentIndex + 1];

    // Handle bold: **text**
    if (char === '*' && nextChar === '*') {
      const endIndex = text.indexOf('**', currentIndex + 2);
      if (endIndex > -1) {
        pushText(buffer);
        buffer = '';
        const boldText = text.substring(currentIndex + 2, endIndex);
        pushElement(
          <strong
            key={`bold-${keyCounter++}`}
            className="font-semibold text-slate-800 dark:text-white"
          >
            {renderSimpleMarkdown(boldText)}
          </strong>
        );
        currentIndex = endIndex + 2;
        continue;
      }
    }

    // Handle italic: *text* (but not **)
    if (char === '*' && nextChar !== '*') {
      const endIndex = text.indexOf('*', currentIndex + 1);
      if (endIndex > -1 && text[endIndex - 1] !== '*') {
        pushText(buffer);
        buffer = '';
        const italicText = text.substring(currentIndex + 1, endIndex);
        pushElement(
          <em key={`italic-${keyCounter++}`} className="italic text-slate-700 dark:text-white">
            {renderSimpleMarkdown(italicText)}
          </em>
        );
        currentIndex = endIndex + 1;
        continue;
      }
    }

    // Handle italic: _text_
    if (char === '_') {
      const endIndex = text.indexOf('_', currentIndex + 1);
      if (endIndex > -1) {
        pushText(buffer);
        buffer = '';
        const italicText = text.substring(currentIndex + 1, endIndex);
        pushElement(
          <em key={`italic-${keyCounter++}`} className="italic text-slate-700 dark:text-white">
            {renderSimpleMarkdown(italicText)}
          </em>
        );
        currentIndex = endIndex + 1;
        continue;
      }
    }

    // Handle inline code: `text`
    if (char === '`') {
      const endIndex = text.indexOf('`', currentIndex + 1);
      if (endIndex > -1) {
        pushText(buffer);
        buffer = '';
        const codeText = text.substring(currentIndex + 1, endIndex);
        pushElement(
          <code
            key={`code-${keyCounter++}`}
            className="px-1 py-0.5 bg-slate-200 dark:bg-slate-800/50 text-amber-600 dark:text-amber-400 rounded text-[0.85em] font-mono"
          >
            {codeText}
          </code>
        );
        currentIndex = endIndex + 1;
        continue;
      }
    }

    // Handle links: [text](url)
    if (char === '[') {
      const labelEnd = text.indexOf(']', currentIndex);
      if (labelEnd > -1 && text[labelEnd + 1] === '(') {
        const urlEnd = text.indexOf(')', labelEnd + 2);
        if (urlEnd > -1) {
          pushText(buffer);
          buffer = '';
          const label = text.substring(currentIndex + 1, labelEnd);
          const url = text.substring(labelEnd + 2, urlEnd);
          pushElement(
            <a
              key={`link-${keyCounter++}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-500 hover:text-amber-400 underline underline-offset-2"
            >
              {label}
            </a>
          );
          currentIndex = urlEnd + 1;
          continue;
        }
      }
    }

    // Regular character
    buffer += char;
    currentIndex++;
  }

  pushText(buffer);

  return parts.length === 0 ? null : parts.length === 1 ? parts[0] : <>{parts}</>;
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if text contains markdown syntax
 */
export function hasMarkdown(text: string): boolean {
  if (!text) return false;

  const markdownPatterns = [
    /\*\*[^*]+\*\*/, // bold
    /\*[^*]+\*/, // italic
    /_[^_]+_/, // italic underscore
    /`[^`]+`/, // inline code
    /\[[^\]]+\]\([^)]+\)/, // links
    /^#{1,6}\s/m, // headers
    /^[-*+]\s/m, // unordered list
    /^\d+\.\s/m, // ordered list
    /^>\s/m, // blockquote
    /```/, // code block
  ];

  return markdownPatterns.some(pattern => pattern.test(text));
}

/**
 * Strip markdown syntax from text (for plain text output)
 */
export function stripMarkdown(text: string): string {
  if (!text) return '';

  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1') // bold
    .replace(/\*([^*]+)\*/g, '$1') // italic
    .replace(/_([^_]+)_/g, '$1') // italic underscore
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/^#{1,6}\s+/gm, '') // headers
    .replace(/^[-*+]\s+/gm, '') // unordered list
    .replace(/^\d+\.\s+/gm, '') // ordered list
    .replace(/^>\s+/gm, '') // blockquote
    .replace(/```[\s\S]*?```/g, '') // code blocks
    .trim();
}

export default MarkdownText;
